/**
 * LinkSite — "link a website" from the bottom bar.
 *
 * One field. The server decides whether the page can be framed (submit_link reads its
 * headers — the feed cannot tell a refused frame from a blank page), so this component only
 * tidies the input, shows the server's reason when it says no, and on success hands the card
 * back so it lands in the next slot: one BLITZ away. Since migration 0020 that card is on the
 * bench — next for its maker, in everyone else's feed once a person has opened it on a phone —
 * and the sheet's words say so.
 *
 * Three tabs, shown to everyone: Link a website, Upload, Make one. Link and Upload put the
 * account door in place of the form until there is a handle to add under. Make one is the brief
 * (src/lib/brief.ts) for anyone to read and copy into their own model, and a box to paste the
 * answer back into; the door appears only when an anonymous visitor presses Add, and the pasted
 * answer is kept while they go through it. Adding is the Upload tab's html path exactly.
 */
import { For, Match, Show, Switch, createEffect, createSignal, onCleanup, untrack } from 'solid-js';
import { saveMakeDraft, takeMakeDraft } from '../lib/makeDraft';
import { submitLink, type CardStatus } from '../api/cards';
import { MADE_WITH_MAX, classifyMedia, htmlFile, uploadMedia } from '../api/media';
import { linkGate, type Account } from '../api/account';
import { logUi } from '../api/ui';
import { PAGE_BRIEF, titleOf, unfence } from '../lib/brief';
import type { GameMeta } from '../lib/protocol';

export type SheetMode = 'link' | 'upload' | 'make';

const MODES: ReadonlyArray<[SheetMode, string]> = [
  ['link', 'Link a website'],
  ['upload', 'Upload'],
  ['make', 'Make one'],
];

export interface LinkSiteProps {
  open: boolean;
  onClose: () => void;
  /** The card exists on the server (on the bench unless it was already live); put it next in the feed. */
  onLinked: (meta: GameMeta, existing: boolean, status: CardStatus) => void;
  /** Who is asking. Only an account with a handle may link; everyone else is shown the way in. */
  account: Account;
  /** Open the account sheet in the view that unblocks linking. */
  onNeedAccount: (view: 'create' | 'signin' | 'confirm' | 'profile') => void;
  /** The tab to open on (?make=1 opens on Make one); otherwise the last one used. */
  mode?: SheetMode;
}

export function LinkSite(props: LinkSiteProps) {
  const [value, setValue] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  /** Three ways in: paste a link, upload a photo, a video or an HTML page, or make one with a model. */
  const [mode, setMode] = createSignal<SheetMode>('link');
  const [file, setFile] = createSignal<File | null>(null);
  const [title, setTitle] = createSignal('');
  /** The maker's own word for what made it (a model's name, 'by hand'): optional, never checked. */
  const [madeWith, setMadeWith] = createSignal('');
  /** Pasted HTML — the page a model just wrote, straight from the chat, no file to save. */
  const [pasting, setPasting] = createSignal(false);
  const [pasted, setPasted] = createSignal('');
  /** Make one: the model's answer. Kept across closing the sheet, so a detour through the account door does not lose it. */
  const [made, setMade] = createSignal('');
  /** Make one: what the copy button managed — the clipboard, or only selecting the text for the person to copy. */
  const [copied, setCopied] = createSignal<'copied' | 'selected' | null>(null);
  /** Make one: Add was pressed without an account to add under, so the door is showing. */
  const [door, setDoor] = createSignal(false);
  const gate = () => linkGate(props.account);
  let input: HTMLInputElement | undefined;
  let picker: HTMLInputElement | undefined;
  let briefBox: HTMLTextAreaElement | undefined;

  const choose = (m: SheetMode) => {
    if (m === mode()) return;
    setError(null);
    setMode(m);
    if (m === 'make') logUi('make_open');
  };

  createEffect(() => {
    if (!props.open) return;
    setError(null);
    setFile(null);
    setTitle('');
    setMadeWith('');
    setPasting(false);
    setPasted('');
    setCopied(null);
    setDoor(false);
    const m = props.mode ?? untrack(mode);
    setMode(m);
    if (m === 'make') {
      logUi('make_open');
      // Back from /join or /signin: the answer pasted before the trip is put back.
      const d = takeMakeDraft();
      if (d) {
        setMade(d.made);
        setMadeWith(d.madeWith);
      }
    }
    queueMicrotask(() => input?.focus());
    // Escape closes from anywhere while open. Listening on the form alone misses the case
    // where the submit button was focused and then disabled — focus falls to <body>.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  const submit = async (e: Event) => {
    e.preventDefault();
    if (busy()) return;
    setBusy(true);
    setError(null);
    const r = await submitLink(value());
    setBusy(false);
    if (!r.ok) {
      setError(r.reason);
      input?.focus(); // back to the field: the next move is to fix the address
      return;
    }
    setValue('');
    props.onLinked(r.meta, r.existing, r.status);
  };

  const pick = (f: File | null) => {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    const c = classifyMedia(f);
    if ('error' in c) {
      setFile(null);
      setError(c.error);
      return;
    }
    setFile(f);
  };

  /** What would be uploaded: the chosen file, or the pasted page. */
  const pending = (): File | null => file() ?? (pasting() && pasted().trim() ? htmlFile(pasted()) : null);

  const upload = async (e: Event) => {
    e.preventDefault();
    const f = pending();
    if (!f || busy()) return;
    setBusy(true);
    setError(null);
    const r = await uploadMedia(f, title().trim() || undefined, madeWith().trim() || undefined);
    setBusy(false);
    if (!r.ok) {
      if (r.code === 'account_required') props.onNeedAccount('create');
      else if (r.code === 'handle_required') props.onNeedAccount('profile');
      else setError(r.reason);
      return;
    }
    setFile(null);
    setPasted('');
    setTitle('');
    setMadeWith('');
    props.onLinked(r.meta, r.existing, r.status);
  };

  /** Copy the brief. Where the clipboard is refused (an http page, an old WebView) the text is selected instead. */
  const copyBrief = async () => {
    logUi('brief_copy');
    try {
      await navigator.clipboard.writeText(PAGE_BRIEF);
      setCopied('copied');
      return;
    } catch {
      /* fall back to selecting it */
    }
    briefBox?.focus();
    briefBox?.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      /* selected is the best this browser does */
    }
    setCopied(ok ? 'copied' : 'selected');
  };

  /** A pasted answer in a Markdown fence is the page inside it: unwrap it where the person can see. */
  const onMadeInput = (el: HTMLTextAreaElement) => {
    const v = el.value;
    const page = v.includes('```') ? unfence(v) : v;
    if (page !== v) el.value = page;
    setMade(page);
  };

  const make = async (e: Event) => {
    e.preventDefault();
    const html = unfence(made());
    if (!html || busy()) return;
    if (gate() !== 'none') {
      setDoor(true);
      return;
    }
    setBusy(true);
    setError(null);
    const r = await uploadMedia(htmlFile(html), titleOf(html), madeWith().trim() || undefined);
    setBusy(false);
    if (!r.ok) {
      if (r.code === 'account_required') props.onNeedAccount('create');
      else if (r.code === 'handle_required') props.onNeedAccount('profile');
      else setError(r.reason);
      return;
    }
    setMade('');
    setMadeWith('');
    props.onLinked(r.meta, r.existing, r.status);
  };

  const Tabs = () => (
    <div class="modes" role="tablist" aria-label="What to add">
      <For each={MODES}>
        {([m, label]) => (
          <button
            type="button"
            class={mode() === m ? 'mode active' : 'mode'}
            role="tab"
            aria-selected={mode() === m}
            onClick={() => choose(m)}
            data-testid={`mode-${m}`}
          >
            {label}
          </button>
        )}
      </For>
    </div>
  );

  /**
   * Creating an account and signing in are pages of their own, so these two leave the feed; a
   * Make one answer waits in the tab until Make one opens again (src/lib/makeDraft.ts).
   */
  const leaveFor = (view: 'create' | 'signin') => {
    if (mode() === 'make') saveMakeDraft({ made: made(), madeWith: madeWith() });
    props.onNeedAccount(view);
  };

  /** The way past whatever stands between this visitor and adding: an account, a confirmed email, a handle. */
  const Door = () => (
    <Switch>
      <Match when={gate() === 'account'}>
        <p class="hint">Anyone can look. Adding to the feed takes an account — it goes out under your handle.</p>
        <div class="row">
          <button type="button" class="btn" onClick={() => leaveFor('signin')} data-testid="gate-signin">
            Sign in
          </button>
          <button type="button" class="btn primary" onClick={() => leaveFor('create')} data-testid="gate-create">
            Create account
          </button>
        </div>
      </Match>
      <Match when={gate() === 'confirm'}>
        <p class="hint">Almost there: confirm your email — the link is in your inbox — and adding opens up.</p>
        <div class="row">
          <button type="button" class="btn" onClick={() => props.onClose()}>
            Later
          </button>
          <button type="button" class="btn primary" onClick={() => props.onNeedAccount('confirm')} data-testid="gate-confirm">
            I opened it
          </button>
        </div>
      </Match>
      <Match when={gate() === 'handle'}>
        <p class="hint">Pick a handle first: it is the name on everything you add.</p>
        <div class="row">
          <button type="button" class="btn" onClick={() => props.onClose()}>
            Later
          </button>
          <button type="button" class="btn primary" onClick={() => props.onNeedAccount('profile')} data-testid="gate-handle">
            Pick a handle
          </button>
        </div>
      </Match>
    </Switch>
  );

  return (
    <Show when={props.open}>
      <div class="sheet-veil" onClick={() => props.onClose()} aria-hidden="true" />
      <Switch>
        <Match when={mode() === 'make'}>
          <form class="link-sheet make-sheet" data-ui data-testid="make-sheet" aria-label="Make one" onSubmit={(e) => void make(e)}>
            <Tabs />
            <p class="hint">Ask any model for a page with this. Paste what it gives you back here.</p>
            <textarea
              ref={briefBox}
              class="brief"
              readonly
              rows="5"
              value={PAGE_BRIEF}
              data-testid="make-brief"
              aria-label="The brief"
            />
            <button type="button" class="btn" onClick={() => void copyBrief()} data-testid="make-copy">
              {copied() === 'copied' ? 'Copied — now paste it into a model' : copied() === 'selected' ? 'Selected — copy it from the box' : 'Copy the brief'}
            </button>
            <textarea
              class="answer"
              placeholder="Paste the model's answer here — the whole page"
              rows="4"
              spellcheck={false}
              autocapitalize="off"
              value={made()}
              onInput={(e) => onMadeInput(e.currentTarget)}
              data-testid="make-paste"
              aria-label="The model's answer"
            />
            <input
              type="text"
              placeholder="Made with (optional) — the model"
              maxlength={MADE_WITH_MAX}
              autocapitalize="off"
              value={madeWith()}
              onInput={(e) => setMadeWith(e.currentTarget.value)}
              data-testid="make-made-with"
              aria-label="Made with"
            />
            <Show when={error()}>
              <p class="error" role="alert" data-testid="make-error">{error()}</p>
            </Show>
            <Show
              when={door() && gate() !== 'none'}
              fallback={
                <div class="row">
                  <button type="button" class="btn" onClick={() => props.onClose()}>
                    Cancel
                  </button>
                  <button type="submit" class="btn primary" disabled={busy() || !made().trim()} data-testid="make-submit">
                    {busy() ? 'Adding…' : 'Add to Blitz'}
                  </button>
                </div>
              }
            >
              <div class="door" data-testid="make-door" data-gate={gate()}>
                <Door />
              </div>
            </Show>
          </form>
        </Match>
        <Match when={gate() !== 'none'}>
          <div class="link-sheet" data-ui data-testid="link-gate" data-gate={gate()} role="dialog" aria-label={mode() === 'upload' ? 'Upload' : 'Link a website'}>
            <Tabs />
            <Door />
          </div>
        </Match>
        <Match when={mode() === 'upload'}>
          <form class="link-sheet" data-ui data-testid="upload-sheet" aria-label="Upload a photo, a video or a page" onSubmit={(e) => void upload(e)}>
            <Tabs />
            <p class="hint">
              A photo, a short video, or an HTML page — yours, or something a model wrote for you. It becomes a card under your handle, next in your feed; everyone else sees it once it has been looked at on a phone.
            </p>
            <input
              ref={picker}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,text/html,.html,.htm"
              onChange={(e) => pick(e.currentTarget.files?.[0] ?? null)}
              data-testid="upload-file"
              style={{ display: 'none' }}
            />
            <Show
              when={pasting()}
              fallback={
                <button type="button" class="drop" onClick={() => picker?.click()} data-testid="upload-pick">
                  <Show when={file()} fallback={<span>Choose a photo, a video or an .html file</span>}>
                    {(f) => (
                      <span>
                        <b>{f().name}</b> · {f().size < 1048576 ? `${Math.max(1, Math.round(f().size / 1024))} KB` : `${(f().size / 1048576).toFixed(1)} MB`}
                      </span>
                    )}
                  </Show>
                </button>
              }
            >
              {/* The whole page, as the model gave it: <!doctype html> … </html>. */}
              <textarea
                placeholder="<!doctype html> … paste the whole page"
                rows="6"
                spellcheck={false}
                autocapitalize="off"
                value={pasted()}
                onInput={(e) => setPasted(e.currentTarget.value)}
                data-testid="upload-paste"
                aria-label="HTML to upload"
              />
            </Show>
            <button type="button" class="linkish" onClick={() => setPasting((v) => !v)} data-testid="upload-toggle-paste">
              {pasting() ? 'Choose a file instead' : 'Or paste HTML'}
            </button>
            <input type="text" placeholder="Title (optional)" maxlength="80" value={title()} onInput={(e) => setTitle(e.currentTarget.value)} data-testid="upload-title" aria-label="Title" />
            <input
              type="text"
              placeholder="Made with (optional) — the model, or by hand"
              maxlength={MADE_WITH_MAX}
              autocapitalize="off"
              value={madeWith()}
              onInput={(e) => setMadeWith(e.currentTarget.value)}
              data-testid="upload-made-with"
              aria-label="Made with"
            />
            <Show when={error()}>
              <p class="error" role="alert" data-testid="upload-error">{error()}</p>
            </Show>
            <div class="row">
              <button type="button" class="btn" onClick={() => props.onClose()}>
                Cancel
              </button>
              <button type="submit" class="btn primary" disabled={busy() || !pending()} data-testid="upload-submit">
                {busy() ? 'Uploading…' : 'Add to Blitz'}
              </button>
            </div>
          </form>
        </Match>
        <Match when={mode() === 'link'}>
      <form
        class="link-sheet"
        data-ui
        data-testid="link-sheet"
        aria-label="Link a website"
        onSubmit={(e) => void submit(e)}
      >
        <Tabs />
        <p class="hint">
          Paste a link. If the site allows framing, it becomes a card — next in your feed, and in everyone else's once it has been looked at on a phone. An itch.io, OpenProcessing, CodePen, Scratch, Shadertoy or p5.js page is turned into the platform's own embed.
        </p>
        {/* type="text": a bare hostname is the common paste, and type="url" would reject it. */}
        <input
          ref={input}
          type="text"
          inputmode="url"
          autocomplete="off"
          autocapitalize="off"
          spellcheck={false}
          placeholder="example.com"
          value={value()}
          onInput={(e) => setValue(e.currentTarget.value)}
          data-testid="link-url"
          aria-label="Web address"
        />
        <Show when={error()}>
          <p class="error" role="alert" data-testid="link-error">
            {error()}
          </p>
        </Show>
        <div class="row">
          <button type="button" class="btn" onClick={() => props.onClose()}>
            Cancel
          </button>
          <button type="submit" class="btn primary" disabled={busy()} data-testid="link-submit">
            {busy() ? 'Checking…' : 'Add to Blitz'}
          </button>
        </div>
      </form>
        </Match>
      </Switch>
    </Show>
  );
}
